const READY_SIGNAL = 'proctoringReady_n6EY';
const ORIGIN_PARAM = 'examus-client-origin';
const OVERLAY_ID = 'constructor_iframe_guard_overlay';
const ASSESS_CODE_STORAGE_KEY = 'constructor_iframe_guard_assess_code';

function normalizeOrigin(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }
  try {
    return new URL(raw).origin;
  } catch (_) {
    return raw.replace(/\/+$/, '');
  }
}

function isInIframe() {
  try {
    return window.self !== window.top;
  } catch (_) {
    return true;
  }
}

function isConstructorOrigin(origin) {
  const normalized = normalizeOrigin(origin);
  if (!normalized) {
    return false;
  }
  try {
    const host = new URL(normalized).hostname.toLowerCase();
    return host.endsWith('.proctor.constructor.app');
  } catch (_) {
    return false;
  }
}

function readReferrerOrigin() {
  return normalizeOrigin(document.referrer || '');
}

function readAncestorOrigin() {
  try {
    const list = window.location?.ancestorOrigins;
    if (!list || typeof list.length !== 'number') {
      return '';
    }
    for (let i = 0; i < list.length; i += 1) {
      const origin = normalizeOrigin(list[i]);
      if (isConstructorOrigin(origin)) {
        return origin;
      }
    }
  } catch (_) {
    return '';
  }
  return '';
}

function hasConstructorUserAgent() {
  try {
    const ua = String(navigator.userAgent || '').toLowerCase();
    const hasConstructor = ua.includes('constructor');
    const hasSecureBrowser = /secure[\s_-]?browser/.test(ua);
    const hasConstructorElectron = hasConstructor && ua.includes('electron');
    const hasExamusElectron = ua.includes('examus-electron') || (ua.includes('examus') && ua.includes('electron'));
    return hasSecureBrowser || hasConstructor || hasConstructorElectron || hasExamusElectron;
  } catch (_) {
    return false;
  }
}

function readExpectedOrigin() {
  const params = new URLSearchParams(window.location.search);
  const explicitOrigin = normalizeOrigin(params.get(ORIGIN_PARAM) || '');
  if (explicitOrigin) {
    return explicitOrigin;
  }

  // API flow may not always pass examus-client-origin. For iframe runtime we can
  // fallback to Constructor referrer origin and still require Constructor domain.
  if (!isInIframe()) {
    return '';
  }
  const referrerOrigin = readReferrerOrigin();
  if (isConstructorOrigin(referrerOrigin)) {
    return referrerOrigin;
  }
  const ancestorOrigin = readAncestorOrigin();
  if (isConstructorOrigin(ancestorOrigin)) {
    return ancestorOrigin;
  }
  return '';
}

function parseReadyMessage(data) {
  if (typeof data === 'string') {
    if (data === READY_SIGNAL) {
      return {ready: true, assessCode: ''};
    }
    try {
      const parsed = JSON.parse(data);
      return parseReadyMessage(parsed);
    } catch (_) {
      return {ready: false, assessCode: ''};
    }
  }

  if (!data || typeof data !== 'object') {
    return {ready: false, assessCode: ''};
  }

  const signal = data.type || data.event || data.message || data.action || data.name || '';
  const ready = signal === READY_SIGNAL
    || data.proctoringReady === true
    || data.proctoringIsActive === true
    || data.isProctoringActive === true
    || data.active === true;
  const assessCode = data.assessCode
    || data.assess_code
    || data.examId
    || data.exam_id
    || data?.payload?.assessCode
    || data?.payload?.assess_code
    || '';
  return {ready, assessCode: String(assessCode || '')};
}

function getOrCreateOverlay() {
  let overlay = document.getElementById(OVERLAY_ID);
  if (overlay) {
    return overlay;
  }

  overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.style.position = 'fixed';
  overlay.style.inset = '0';
  overlay.style.zIndex = '30000';
  overlay.style.background = 'rgba(15, 23, 42, 0.9)';
  overlay.style.color = '#f8fafc';
  overlay.style.display = 'flex';
  overlay.style.alignItems = 'center';
  overlay.style.justifyContent = 'center';
  overlay.style.padding = '24px';
  overlay.style.textAlign = 'center';
  overlay.style.fontFamily = 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  overlay.style.fontSize = '16px';
  overlay.style.lineHeight = '1.5';

  const content = document.createElement('div');
  content.style.maxWidth = '760px';
  content.style.background = 'rgba(2, 6, 23, 0.88)';
  content.style.border = '1px solid rgba(148, 163, 184, 0.35)';
  content.style.borderRadius = '12px';
  content.style.padding = '18px 20px';

  const title = document.createElement('div');
  title.dataset.role = 'title';
  title.style.fontSize = '20px';
  title.style.fontWeight = '700';
  title.style.marginBottom = '10px';
  title.innerText = 'Waiting for Constructor Proctoring';

  const details = document.createElement('div');
  details.dataset.role = 'details';
  details.style.opacity = '0.9';
  details.innerText = 'Please keep this page opened inside Constructor proctoring flow.';

  content.appendChild(title);
  content.appendChild(details);
  overlay.appendChild(content);
  document.body.appendChild(overlay);
  return overlay;
}

function setOverlayState({title, details, error = false}) {
  const overlay = getOrCreateOverlay();
  const titleEl = overlay.querySelector('[data-role="title"]');
  const detailsEl = overlay.querySelector('[data-role="details"]');
  if (titleEl) {
    titleEl.innerText = title;
    titleEl.style.color = error ? '#fecaca' : '#f8fafc';
  }
  if (detailsEl) {
    detailsEl.innerText = details;
    detailsEl.style.color = error ? '#fca5a5' : '#e2e8f0';
  }
  overlay.style.display = 'flex';
}

function hideOverlay() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay && overlay.parentElement) {
    overlay.parentElement.removeChild(overlay);
  }
}

export function createConstructorIframeGuard({
  enabled = false,
  timeoutMs = 20000,
  assessCode = '',
  onStateChange = null,
} = {}) {
  let expectedOrigin = '';
  let allowAnyConstructorOrigin = false;
  let ready = !enabled;
  let error = null;
  let settled = false;
  let disposed = false;
  let timeoutId = null;
  const assessCodeScope = String(assessCode || '').trim() || 'default';
  let resolveWait;
  const readyPromise = new Promise((resolve) => {
    resolveWait = resolve;
  });

  function emitState(state) {
    if (typeof onStateChange === 'function') {
      onStateChange({
        enabled,
        ready,
        error,
        expectedOrigin,
        ...state,
      });
    }
  }

  function settle(result) {
    if (settled) {
      return;
    }
    settled = true;
    resolveWait(result);
  }

  function setError(code, message) {
    if (disposed || ready) {
      return;
    }
    error = {code, message};
    setOverlayState({
      title: 'External Proctoring Context Error',
      details: message,
      error: true,
    });
    emitState({stage: 'error'});
    settle(false);
  }

  function validateAssessCode(receivedAssessCode) {
    const incoming = String(receivedAssessCode || '').trim();
    if (!incoming) {
      return true;
    }
    const storageKey = `${ASSESS_CODE_STORAGE_KEY}:${assessCodeScope}`;
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (!saved) {
        sessionStorage.setItem(storageKey, incoming);
        return true;
      }
      return saved === incoming;
    } catch (_) {
      return true;
    }
  }

  function cleanup() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    window.removeEventListener('message', handleMessage, false);
  }

  function markReady() {
    if (disposed || ready) {
      return;
    }
    ready = true;
    cleanup();
    hideOverlay();
    emitState({stage: 'ready'});
    settle(true);
  }

  function handleMessage(event) {
    if (disposed || ready || !expectedOrigin) {
      return;
    }
    const incomingOrigin = normalizeOrigin(event.origin);
    if (allowAnyConstructorOrigin) {
      if (!isConstructorOrigin(incomingOrigin)) {
        return;
      }
    } else if (incomingOrigin !== expectedOrigin) {
      return;
    }
    const parsed = parseReadyMessage(event.data);
    emitState({
      stage: 'message',
      incomingOrigin,
      parsedReady: parsed.ready,
      parsedAssessCode: parsed.assessCode || '',
    });
    if (!parsed.ready) {
      return;
    }
    if (!validateAssessCode(parsed.assessCode)) {
      setError(
        'invalid_assess_code',
        'Constructor assessCode mismatch. Please reopen exam from Constructor proctoring page.'
      );
      return;
    }
    markReady();
  }

  if (!enabled) {
    settle(true);
    emitState({stage: 'disabled'});
    return {
      isEnabled: () => false,
      isReady: () => true,
      getError: () => null,
      getExpectedOrigin: () => '',
      waitUntilReady: () => readyPromise,
      dispose: () => {},
    };
  }

  setOverlayState({
    title: 'Waiting for Constructor Proctoring',
    details: 'Checking iframe context and waiting for proctoring handshake...',
    error: false,
  });

  expectedOrigin = readExpectedOrigin();
  const secureBrowserUaFallback = !expectedOrigin && isInIframe() && hasConstructorUserAgent();
  if (!expectedOrigin) {
    if (secureBrowserUaFallback) {
      expectedOrigin = 'constructor-domain-fallback';
      allowAnyConstructorOrigin = true;
      emitState({
        stage: 'init',
        allowAnyConstructorOrigin,
        expectedOrigin,
        secureBrowserUaFallback: true,
      });
      window.addEventListener('message', handleMessage, false);
      timeoutId = setTimeout(() => {
        setError(
          'handshake_timeout',
          'Constructor proctoring handshake timed out. Reload from Constructor page and try again.'
        );
      }, timeoutMs);
      emitState({stage: 'waiting'});
    } else {
      setError(
        'missing_examus_client_origin',
        'Missing Constructor iframe context. Please open the exam from Constructor proctoring page.'
      );
    }
  } else {
    allowAnyConstructorOrigin = !normalizeOrigin(new URLSearchParams(window.location.search).get(ORIGIN_PARAM) || '');
    emitState({
      stage: 'init',
      allowAnyConstructorOrigin,
      expectedOrigin,
    });
    window.addEventListener('message', handleMessage, false);
    timeoutId = setTimeout(() => {
      setError(
        'handshake_timeout',
        'Constructor proctoring handshake timed out. Reload from Constructor page and try again.'
      );
    }, timeoutMs);
    emitState({stage: 'waiting'});
  }

  return {
    isEnabled: () => enabled,
    isReady: () => ready,
    getError: () => error,
    getExpectedOrigin: () => expectedOrigin,
    waitUntilReady: () => readyPromise,
    dispose: () => {
      if (disposed) {
        return;
      }
      disposed = true;
      cleanup();
      hideOverlay();
      if (!ready) {
        settle(false);
      }
      emitState({stage: 'disposed'});
    },
  };
}
